import { WSQueries } from "../../../../query/WSQuery";
import orderBy from "lodash/orderBy";
import { queriesEngagements } from "@query";
import {
  WSEmptyState,
  WSFilters,
  WSPage,
  WSPageToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  EngagementRowsTable,
  EngagementListSort
} from "../../components/EngagementRowsTable";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { useCreateEngagementModal } from "../../modals/CreateEngagementModal";
import React, { useState } from "react";
import { Tabs } from "../../../../components/Tabs";
import { getSectionPathByEngagementStatus } from "../../paths";
import { EngagementStatus } from "@wingspanhq/wingspan-api-sdk";
import { Route, Switch } from "react-router-dom";

const EngagementTypesTab: React.FC<{
  engagementStatus: EngagementStatus;
  isMainTab?: boolean;
}> = ({ engagementStatus, isMainTab }) => {
  const modalCreateEngagement = useCreateEngagementModal();
  const [sort, setSort] = useState<EngagementListSort>({
    field: "name",
    direction: "asc"
  });
  const queryEngagementList = queriesEngagements.listEngagements.useQueryAll(
    { filter: { status: { anyOf: [engagementStatus] } } },
    { refetchOnMount: false, retry: false }
  );

  return (
    <WSQueries queries={{ queryEngagementList }}>
      {() => {
        const engagements = (queryEngagementList.flatData ?? []).filter(
          e => !e.isDefault
        );
        const list =
          sort.field === "name"
            ? orderBy(
                engagements,
                [e => (e.name ?? "").toLowerCase()],
                [sort.direction]
              )
            : orderBy(
                engagements,
                [e => e.events?.updatedAt],
                [sort.direction]
              );

        return (
          <>
            <WSFilters
              my="L"
              values={{}}
              onFilter={newValues => {}}
              info={{
                count: list.length
              }}
              quickFilters={[]}
            />
            {list.length === 0 ? (
              <WSEmptyState
                py="3XL"
                hasBorder
                type="documents"
                title="No engagements yet"
                bgColor="gray"
                description={
                  isMainTab
                    ? "Once an engagement is added, it will appear here."
                    : undefined
                }
                actions={
                  isMainTab
                    ? {
                        buttons: [
                          {
                            label: "Add engagement",
                            kind: "Secondary",
                            onClick: async () => {
                              await modalCreateEngagement.open();
                            }
                          }
                        ]
                      }
                    : undefined
                }
              />
            ) : (
              <EngagementRowsTable
                mt="M"
                engagements={list}
                sort={sort}
                onSort={setSort}
              />
            )}
          </>
        );
      }}
    </WSQueries>
  );
};

export const RouteEngagementTypes: React.FC = () => {
  useBrowserPageTitle("Engagements");

  const modalCreateEngagement = useCreateEngagementModal();

  const queryActiveEngagementTypeSummary =
    queriesEngagements.listEngagements.useQueryAll(
      { filter: { status: { anyOf: [EngagementStatus.ACTIVE] } } },
      { refetchOnMount: false, retry: false }
    );

  const queryArchivedEngagementTypeSummary =
    queriesEngagements.listEngagements.useQueryAll(
      { filter: { status: { anyOf: [EngagementStatus.ARCHIVED] } } },
      { refetchOnMount: false, retry: false }
    );

  return (
    <WSPage>
      <WSPageToolbar
        title={"Engagements"}
        actions={[
          {
            label: "Add engagement",
            onClick: async () => {
              await modalCreateEngagement.open();
            },
            buttonKind: "Primary"
          }
        ]}
      >
        <WSText.ParagraphSm color="gray500" style={{ maxWidth: "550px" }}>
          Engagements represent a defined scope of work—like a project, or
          department—that can be assigned to contractors and can include
          specific requirements.
        </WSText.ParagraphSm>
      </WSPageToolbar>

      <WSQueries
        queries={{
          queryActiveEngagementTypeSummary,
          queryArchivedEngagementTypeSummary
        }}
      >
        {() => {
          const activeCount = (
            queryActiveEngagementTypeSummary.flatData ?? []
          ).filter(e => !e.isDefault).length;
          const archivedCount = (
            queryArchivedEngagementTypeSummary.flatData ?? []
          ).filter(e => !e.isDefault).length;
          const tabs = [
            {
              label: "Active",
              path: getSectionPathByEngagementStatus(EngagementStatus.ACTIVE),
              badge: activeCount,
              engagementStatus: EngagementStatus.ACTIVE,
              isMainTab: true
            },
            {
              label: "Archived",
              path: getSectionPathByEngagementStatus(EngagementStatus.ARCHIVED),
              badge: archivedCount,
              engagementStatus: EngagementStatus.ARCHIVED
            }
          ];

          return (
            <>
              <Tabs mb="L" tabs={tabs} />
              <Switch>
                {tabs.map(tab => (
                  <Route
                    key={tab.path}
                    path={tab.path}
                    render={() => (
                      <EngagementTypesTab
                        engagementStatus={tab.engagementStatus}
                        isMainTab={tab.isMainTab}
                      />
                    )}
                  />
                ))}
              </Switch>
            </>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
